<template>
  <v-container>
    <v-row align="center" class="mb-3">
      <v-col>
        <h2 class="text-h5 font-weight-medium">EDI Workflow</h2>
      </v-col>
    </v-row>

    <!-- ── Reference Files ────────────────────────────────────────────────── -->
    <v-card flat outlined class="mb-4">
      <v-card-title class="text-subtitle-1 font-weight-medium py-2 px-4">
        <v-icon left small>mdi-database</v-icon>Reference Files
      </v-card-title>
      <v-divider />
      <v-card-text class="py-3">
        <v-row dense>
          <v-col cols="12" md="6">
            <div class="d-flex align-center">
              <v-icon :color="refFiles.census && refFiles.census.exists ? 'green' : 'grey darken-1'" class="mr-2">
                {{ refFiles.census && refFiles.census.exists ? 'mdi-check-circle' : 'mdi-alert-circle-outline' }}
              </v-icon>
              <div class="flex-grow-1">
                <div class="text-body-2 font-weight-medium">Members Census <span class="grey--text">.xlsx</span></div>
                <div v-if="refFiles.census && refFiles.census.exists" class="text-caption grey--text">
                  {{ refFiles.census.filename }} &nbsp;·&nbsp; {{ formatSize(refFiles.census.size) }}
                  &nbsp;·&nbsp; {{ formatDate(refFiles.census.mtime) }}
                </div>
                <div v-else class="text-caption red--text">Not uploaded</div>
              </div>
              <v-btn small text color="primary" class="ml-2" @click="pickFile('census')">
                <v-icon small left>mdi-upload</v-icon>{{ refFiles.census && refFiles.census.exists ? 'Replace' : 'Upload' }}
              </v-btn>
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="d-flex align-center">
              <v-icon :color="refFiles.providers && refFiles.providers.exists ? 'green' : 'grey darken-1'" class="mr-2">
                {{ refFiles.providers && refFiles.providers.exists ? 'mdi-check-circle' : 'mdi-alert-circle-outline' }}
              </v-icon>
              <div class="flex-grow-1">
                <div class="text-body-2 font-weight-medium">Provider Roster <span class="grey--text">CrystalReportViewer.csv</span></div>
                <div v-if="refFiles.providers && refFiles.providers.exists" class="text-caption grey--text">
                  {{ refFiles.providers.filename }} &nbsp;·&nbsp; {{ formatSize(refFiles.providers.size) }}
                  &nbsp;·&nbsp; {{ formatDate(refFiles.providers.mtime) }}
                </div>
                <div v-else class="text-caption orange--text">Not uploaded (optional)</div>
              </div>
              <v-btn small text color="primary" class="ml-2" @click="pickFile('providers')">
                <v-icon small left>mdi-upload</v-icon>{{ refFiles.providers && refFiles.providers.exists ? 'Replace' : 'Upload' }}
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- ── Generate EDI ───────────────────────────────────────────────────── -->
    <v-card flat outlined class="mb-4">
      <v-card-title class="text-subtitle-1 font-weight-medium py-2 px-4">
        <v-icon left small>mdi-cog-outline</v-icon>Generate EDI
      </v-card-title>
      <v-divider />
      <v-card-text class="py-3">
        <v-row dense align="center">
          <v-col cols="12" sm="6" md="5">
            <v-file-input
              v-model="csvFile"
              label="Claims Detailed CSV"
              accept=".csv,.CSV"
              outlined dense hide-details
              prepend-icon="mdi-file-delimited-outline"
            />
          </v-col>
          <v-col cols="auto">
            <v-btn color="primary" :loading="processing" :disabled="!csvFile || !(refFiles.census && refFiles.census.exists)"
              @click="processCSV">
              <v-icon left small>mdi-play</v-icon>Process & Generate
            </v-btn>
          </v-col>
          <v-col v-if="!(refFiles.census && refFiles.census.exists)" cols="auto">
            <span class="text-caption red--text">Upload Members Census first</span>
          </v-col>
        </v-row>

        <v-expand-transition>
          <div v-if="processing && jobProgress" class="mt-3">
            <v-alert dense text type="info" class="mb-0">
              <v-progress-circular indeterminate size="16" width="2" class="mr-2" />
              {{ jobProgress }}
            </v-alert>
          </div>
        </v-expand-transition>

        <v-expand-transition>
          <div v-if="lastResult" class="mt-3">
            <v-alert
              :type="lastResult.ok ? 'success' : 'error'"
              dense text class="mb-2"
            >
              <span v-if="lastResult.ok">
                Processed {{ lastResult.claimsProcessed }} claims:
                <strong>{{ lastResult.included }}</strong> included,
                <strong>{{ lastResult.skipped }}</strong> skipped
                <span v-if="lastResult.lockedSkipped > 0">, {{ lastResult.lockedSkipped }} locked (still awaiting FA)</span>.
              </span>
              <span v-else>{{ lastResult.error }}</span>
            </v-alert>
            <div v-if="lastResult.ok" class="d-flex flex-wrap" style="gap:8px">
              <v-chip v-if="lastResult.generatedP" small outlined color="blue">
                <v-icon left x-small>mdi-file</v-icon>
                {{ lastResult.generatedP.file }} — {{ lastResult.generatedP.claim_count }} claims
              </v-chip>
              <v-chip v-if="lastResult.generatedI" small outlined color="purple">
                <v-icon left x-small>mdi-file</v-icon>
                {{ lastResult.generatedI.file }} — {{ lastResult.generatedI.claim_count }} claims
              </v-chip>
            </div>
          </div>
        </v-expand-transition>
      </v-card-text>
    </v-card>

    <!-- ── Generated Files ────────────────────────────────────────────────── -->
    <v-card flat outlined class="mb-4">
      <v-card-title class="text-subtitle-1 font-weight-medium py-2 px-4">
        <v-icon left small>mdi-file-send-outline</v-icon>Generated EDI Files
        <v-spacer />
        <v-btn icon small @click="loadGeneratedFiles" :loading="loadingFiles">
          <v-icon small>mdi-refresh</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />
      <v-data-table
        :headers="fileHeaders"
        :items="generatedFiles"
        :loading="loadingFiles"
        :footer-props="{ 'items-per-page-options': [10, 25, -1] }"
        :items-per-page="10"
        :expanded.sync="expandedFiles"
        show-expand
        sort-by="SUBMIT_DATE"
        sort-desc
        dense
        loading-text="Loading..."
        no-data-text="No files generated yet"
        class="elevation-0"
      >
        <template v-slot:item.FILE_TYPE="{ item }">
          <v-chip x-small :color="item.FILE_TYPE === '837P' ? 'blue lighten-4' : 'purple lighten-4'">
            {{ item.FILE_TYPE }}
          </v-chip>
        </template>

        <template v-slot:item.STATUS="{ item }">
          <v-chip x-small dark :color="batchStatusColor(item.STATUS)">
            {{ item.STATUS }}
          </v-chip>
        </template>

        <template v-slot:item.SUBMIT_DATE="{ item }">
          {{ formatDate(item.SUBMIT_DATE) }}
        </template>

        <template v-slot:item.FA_DATE="{ item }">
          {{ item.FA_DATE ? formatDate(item.FA_DATE) : '—' }}
        </template>

        <template v-slot:item.onAzure="{ item }">
          <v-icon small :color="item.onAzure ? 'green' : (item.onDisk ? 'orange' : 'grey')"
            :title="item.onAzure ? 'On Azure' : (item.onDisk ? 'Local only' : 'Not found')">
            {{ item.onAzure ? 'mdi-cloud-check' : (item.onDisk ? 'mdi-harddisk' : 'mdi-cloud-off-outline') }}
          </v-icon>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn v-if="item.available" icon x-small title="Download" @click="downloadFile(item.BATCH_FILE)" class="mr-1">
            <v-icon x-small color="primary">mdi-download</v-icon>
          </v-btn>
          <v-btn v-if="item.STATUS === 'generated'" icon x-small title="Mark as submitted to NYSDOH"
            @click="markSubmitted(item)" class="mr-1">
            <v-icon x-small color="blue">mdi-send-check</v-icon>
          </v-btn>
          <v-btn icon x-small title="Import FA response" @click="openFaDialog(item)">
            <v-icon x-small color="orange">mdi-file-import-outline</v-icon>
          </v-btn>
        </template>

        <template v-slot:expanded-item="{ headers: h, item }">
          <td :colspan="h.length" class="pa-3 grey lighten-5">
            <div v-if="!item.FA_ERRORS || !item.FA_ERRORS.length" class="text-body-2 grey--text">
              {{ item.FA_FILE ? 'No errors — FA accepted.' : 'No FA imported yet.' }}
            </div>
            <div v-else>
              <div class="text-caption font-weight-medium mb-2">
                <v-icon small class="mr-1" color="error">mdi-alert-circle</v-icon>
                {{ item.FA_ERRORS.length }} segment error(s) — FA: {{ item.FA_FILE }}
                · Accepted: {{ item.FA_ACCEPTED }} · Rejected: {{ item.FA_REJECTED }}
              </div>
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th>Segment</th><th>Pos</th><th>Loop</th><th>Error</th><th>Element</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(err, i) in item.FA_ERRORS" :key="i">
                    <td><strong>{{ err.segment }}</strong></td>
                    <td>{{ err.position }}</td>
                    <td>{{ err.loop }}</td>
                    <td class="text-caption">{{ ik3Label(err.errorCode) }}</td>
                    <td>
                      <span v-if="!err.elements || !err.elements.length">—</span>
                      <v-chip v-for="(el, j) in err.elements" :key="j" x-small outlined class="mr-1 mb-1">
                        {{ el.pos }}: {{ ik4Label(el.code) }}
                        <span v-if="el.value" class="ml-1 red--text">({{ el.value }})</span>
                      </v-chip>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </td>
        </template>
      </v-data-table>
    </v-card>

    <!-- ── Claims ─────────────────────────────────────────────────────────── -->
    <v-card flat outlined>
      <v-card-title class="text-subtitle-1 font-weight-medium py-2 px-4">
        <v-icon left small>mdi-format-list-bulleted</v-icon>Claims
        <v-spacer />
        <v-select
          v-model="claimStatusFilter"
          :items="['all', 'submitted', 'completed', 'rejected', 'error']"
          label="Status"
          outlined dense hide-details
          style="max-width:120px"
          class="mr-2"
        />
        <v-select
          v-model="claimTypeFilter"
          :items="['all', '837P', '837I']"
          label="Type"
          outlined dense hide-details
          style="max-width:100px"
          class="mr-2"
        />
        <v-btn
          v-if="claimCount('error') > 0"
          small text color="orange darken-2"
          :loading="retryingErrors"
          @click="retryErrors"
          class="mr-1"
        >
          <v-icon small left>mdi-reload-alert</v-icon>
          Retry errors ({{ claimCount('error') }})
        </v-btn>
        <v-btn icon small @click="loadClaims" :loading="loadingClaims">
          <v-icon small>mdi-refresh</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />

      <v-tabs v-model="claimTab" height="36">
        <v-tab v-for="t in claimTabs" :key="t.value" style="font-size:12px">
          {{ t.label }}
          <v-chip x-small class="ml-1" :color="t.color" dark>{{ claimCount(t.value) }}</v-chip>
        </v-tab>
      </v-tabs>
      <v-divider />

      <v-data-table
        :headers="claimHeaders"
        :items="claims"
        :server-items-length="totalClaims"
        :page.sync="currentPage"
        :items-per-page.sync="pageSize"
        :loading="loadingClaims"
        :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        @update:page="onPageChange"
        @update:items-per-page="onPageSizeChange"
        @update:sort-by="onSortChange"
        @update:sort-desc="onSortChange"
        dense
        loading-text="Loading claims..."
        no-data-text="No claims"
        class="elevation-0"
      >
        <template v-slot:item.CLAIM_TYPE="{ item }">
          <v-chip x-small :color="item.CLAIM_TYPE === 'CMS' ? 'blue lighten-4' : 'purple lighten-4'">
            {{ item.CLAIM_TYPE === 'CMS' ? '837P' : '837I' }}
          </v-chip>
        </template>

        <template v-slot:item.STATUS="{ item }">
          <v-chip x-small dark :color="claimStatusColor(item.STATUS)">
            <v-icon v-if="item.LOCKED" x-small left>mdi-lock</v-icon>
            {{ item.STATUS }}
          </v-chip>
        </template>

        <template v-slot:item.BILLED="{ item }">
          {{ item.BILLED ? '$' + Number(item.BILLED).toFixed(2) : '—' }}
        </template>

        <template v-slot:item.UPLOAD_DATE="{ item }">
          {{ formatDate(item.UPLOAD_DATE) }}
        </template>

        <template v-slot:item.SERVICE_FROM="{ item }">
          {{ item.SERVICE_FROM || '—' }}
        </template>

        <template v-slot:item.SKIP_REASON="{ item }">
          <span v-if="item.SKIP_REASON" class="text-caption red--text">{{ item.SKIP_REASON }}</span>
          <span v-else-if="item.LOCKED" class="text-caption blue--text">Awaiting FA</span>
          <span v-else>—</span>
        </template>
      </v-data-table>
    </v-card>

    <!-- ── Hidden file inputs ─────────────────────────────────────────────── -->
    <input ref="inputCensus" type="file" accept=".xlsx,.xls" style="display:none" @change="onRefFileChange('census', $event)" />
    <input ref="inputProviders" type="file" accept=".csv" style="display:none" @change="onRefFileChange('providers', $event)" />

    <!-- ── FA Import Dialog ───────────────────────────────────────────────── -->
    <v-dialog v-model="faDialog" max-width="560px" persistent>
      <v-card>
        <v-toolbar dark color="secondary" flat>
          <v-toolbar-title>Import FA Response</v-toolbar-title>
          <v-spacer />
          <v-btn icon dark @click="faDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <p class="text-body-2 mb-3">
            Batch: <strong>{{ faBatch && faBatch.BATCH_FILE || '—' }}</strong>
            <span v-if="faBatch" class="ml-2">
              <v-chip x-small :color="faBatch.FILE_TYPE === '837P' ? 'blue lighten-4' : 'purple lighten-4'">
                {{ faBatch.FILE_TYPE }}
              </v-chip>
            </span>
          </p>
          <v-file-input
            v-model="faFile"
            label="FA .DAT file"
            accept=".dat,.DAT,.txt"
            outlined dense
            prepend-icon="mdi-paperclip"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="faDialog = false; faFile = null">Cancel</v-btn>
          <v-btn color="secondary" :loading="importingFa" :disabled="!faFile" @click="doImportFa">
            Import FA
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snack.show" :color="snack.color" :timeout="5000">
      {{ snack.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from 'axios';

const IK3 = {
  '1': 'Unrecognized segment ID',   '2': 'Unexpected segment',
  '3': 'Mandatory segment missing', '4': 'Loop not in proper sequence',
  '5': 'Loop exceeds max use',      '6': 'Segment exceeds max use',
  '7': 'Segment not in sequence',   '8': 'Data element errors',
};
const IK4 = {
  '1': 'Mandatory element missing', '2': 'Conditional element missing',
  '3': 'Too many elements',         '4': 'Element too short',
  '5': 'Element too long',          '6': 'Invalid character',
  '7': 'Invalid code value',        '8': 'Invalid date',
  '9': 'Invalid time',              '10': 'Exclusion condition violated',
  'I6': 'Invalid',                  'I10': 'Invalid for this context',
  'I11': 'Business rule violation', 'I12': 'Invalid code combination',
};

export default {
  name: 'EdiWorkflow',
  data() {
    return {
      // Reference files
      refFiles: { census: null, providers: null },
      uploadingRef: null,

      // Generate
      csvFile: null,
      processing: false,
      jobProgress: '',
      lastResult: null,

      // Generated files table
      loadingFiles: false,
      generatedFiles: [],
      expandedFiles: [],
      fileHeaders: [
        { text: 'File',        value: 'BATCH_FILE' },
        { text: 'Type',        value: 'FILE_TYPE',   width: 80 },
        { text: 'Claims',      value: 'CLAIM_COUNT', width: 80, align: 'center' },
        { text: 'Status',      value: 'STATUS',      width: 110 },
        { text: 'Generated',   value: 'SUBMIT_DATE', width: 130 },
        { text: 'FA Date',     value: 'FA_DATE',     width: 130 },
        { text: 'Accepted',    value: 'FA_ACCEPTED', width: 80, align: 'center' },
        { text: 'Rejected',    value: 'FA_REJECTED', width: 80, align: 'center' },
        { text: 'Azure',       value: 'onAzure',     width: 70, align: 'center', sortable: false },
        { text: 'Actions',     value: 'actions',     width: 110, sortable: false },
        { text: '',            value: 'data-table-expand' },
      ],

      // FA import
      faDialog: false,
      faBatch: null,
      faFile: null,
      importingFa: false,
      retryingErrors: false,

      // Claims — server-side pagination
      loadingClaims: false,
      claims: [],
      totalClaims: 0,
      statusCounts: { all: 0, submitted: 0, completed: 0, rejected: 0, error: 0 },
      currentPage: 1,
      pageSize: 50,
      sortBy: 'UPLOAD_DATE',
      sortDesc: true,
      pageCache: {},
      claimTab: 0,
      claimTypeFilter: 'all',
      claimStatusFilter: 'all',
      claimTabs: [
        { label: 'All',       value: 'all',       color: 'grey' },
        { label: 'Submitted', value: 'submitted',  color: 'blue' },
        { label: 'Completed', value: 'completed',  color: 'green' },
        { label: 'Rejected',  value: 'rejected',   color: 'red' },
        { label: 'Error',     value: 'error',      color: 'orange' },
      ],
      claimHeaders: [
        { text: 'Claim ID',     value: 'CLAIM_ID',     width: 130 },
        { text: 'Member',       value: 'MEMBER_ID',    width: 100 },
        { text: 'Type',         value: 'CLAIM_TYPE',   width: 80 },
        { text: 'Service Date', value: 'SERVICE_FROM', width: 120 },
        { text: 'Billed',       value: 'BILLED',       width: 100, align: 'right' },
        { text: 'Status',       value: 'STATUS',       width: 130 },
        { text: 'Batch File',   value: 'BATCH_FILE',   width: 200 },
        { text: 'Uploaded',     value: 'UPLOAD_DATE',  width: 120 },
        { text: 'Note',         value: 'SKIP_REASON',  sortable: false },
      ],

      snack: { show: false, text: '', color: 'success' },
    };
  },
  computed: {},
  watch: {
    claimStatusFilter(val) {
      var idx = this.claimTabs.findIndex(function(t) { return t.value === val; });
      if (idx !== -1 && idx !== this.claimTab) this.claimTab = idx;
      this.resetAndLoad();
    },
    claimTab(val) {
      var tabVal = this.claimTabs[val] ? this.claimTabs[val].value : 'all';
      if (this.claimStatusFilter !== tabVal) this.claimStatusFilter = tabVal;
    },
    claimTypeFilter() {
      this.resetAndLoad();
    },
  },
  methods: {
    // ── Reference files ──────────────────────────────────────────────────
    async loadRefStatus() {
      try {
        const res = await axios.get('/api/edi/workflow/status');
        this.refFiles = res.data;
      } catch {
        this.showSnack('Failed to load file status', 'error');
      }
    },
    pickFile(type) {
      if (type === 'census') this.$refs.inputCensus.click();
      else this.$refs.inputProviders.click();
    },
    async onRefFileChange(type, event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      event.target.value = '';

      const endpoint = type === 'census' ? '/api/edi/workflow/upload-census' : '/api/edi/workflow/upload-providers';
      const form = new FormData();
      form.append('file', file);

      this.uploadingRef = type;
      try {
        const res = await axios.post(endpoint, form, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (type === 'census') this.refFiles.census = res.data;
        else this.refFiles.providers = res.data;
        this.showSnack(`${type === 'census' ? 'Members census' : 'Provider roster'} updated`);
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.error) || 'Upload failed';
        this.showSnack(msg, 'error');
      } finally {
        this.uploadingRef = null;
      }
    },

    // ── Generate ─────────────────────────────────────────────────────────
    async processCSV() {
      if (!this.csvFile) return;
      this.processing = true;
      this.lastResult = null;
      this.jobProgress = 'Uploading file...';
      const form = new FormData();
      form.append('file', this.csvFile);
      try {
        const res = await axios.post('/api/edi/workflow/process-csv', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        this.csvFile = null;
        var result = await this.pollJob(res.data.jobId, function(p) { this.jobProgress = p; }.bind(this));
        this.lastResult = result;
        this.jobProgress = '';
        await Promise.all([this.loadGeneratedFiles(), this.loadClaims()]);
        this.showSnack('Processing complete');
      } catch (e) {
        var msg = (e.response && e.response.data && e.response.data.error) || e.message || 'Processing failed';
        this.lastResult = { ok: false, error: msg };
        this.jobProgress = '';
        this.showSnack(msg, 'error');
      } finally {
        this.processing = false;
      }
    },
    pollJob(jobId, onProgress) {
      var self = this;
      return new Promise(function(resolve, reject) {
        var timer = setInterval(async function() {
          try {
            var res = await axios.get('/api/edi/workflow/job/' + jobId);
            var job = res.data;
            if (onProgress) onProgress(job.progress || '');
            if (job.status === 'done') {
              clearInterval(timer);
              resolve(job.result);
            } else if (job.status === 'error') {
              clearInterval(timer);
              reject(new Error(job.error || 'Processing failed'));
            }
          } catch (e) {
            clearInterval(timer);
            reject(e);
          }
        }, 3000);
      });
    },

    // ── Generated files ───────────────────────────────────────────────────
    async loadGeneratedFiles() {
      this.loadingFiles = true;
      try {
        const res = await axios.get('/api/edi/workflow/generated-files');
        this.generatedFiles = res.data;
      } catch {
        this.showSnack('Failed to load files', 'error');
      } finally {
        this.loadingFiles = false;
      }
    },
    downloadFile(filename) {
      window.open(`/api/edi/workflow/download/${encodeURIComponent(filename)}`, '_blank');
    },
    async markSubmitted(item) {
      try {
        await axios.post(`/api/edi/workflow/mark-submitted/${item._id}`);
        item.STATUS = 'submitted';
        this.showSnack(`${item.BATCH_FILE} marked as submitted`);
      } catch {
        this.showSnack('Failed to update status', 'error');
      }
    },
    openFaDialog(item) {
      this.faBatch  = item;
      this.faFile   = null;
      this.faDialog = true;
    },
    async doImportFa() {
      if (!this.faFile) return;
      this.importingFa = true;
      const form = new FormData();
      form.append('file', this.faFile);
      if (this.faBatch) form.append('batchId', this.faBatch._id);
      try {
        const res = await axios.post('/api/edi/workflow/import-fa', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        this.faDialog = false;
        this.faFile   = null;
        await Promise.all([this.loadGeneratedFiles(), this.loadClaims()]);
        const d = res.data;
        this.showSnack(
          `FA imported — status: ${d.faStatus} · Accepted: ${d.accepted} · Rejected: ${d.rejected}` +
          (d.errorCount > 0 ? ` · ${d.errorCount} errors` : ''),
          d.faStatus === 'accepted' ? 'success' : 'warning'
        );
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.error) || 'FA import failed';
        this.showSnack(msg, 'error');
      } finally {
        this.importingFa = false;
      }
    },

    // ── Claims ────────────────────────────────────────────────────────────
    cacheKey(page, pageSize) {
      return this.claimStatusFilter + '|' + this.claimTypeFilter + '|' + page + '|' + pageSize + '|' + this.sortBy + '|' + (this.sortDesc ? '1' : '0');
    },
    async loadClaims() {
      this.pageCache = {};
      await this.fetchPage(this.currentPage, true);
    },
    async resetAndLoad() {
      this.pageCache = {};
      this.currentPage = 1;
      await this.fetchPage(1, true);
    },
    async fetchPage(page, withCounts) {
      var key = this.cacheKey(page, this.pageSize);
      if (this.pageCache[key] && !withCounts) {
        this.claims = this.pageCache[key];
        this.prefetchNeighbors(page);
        return;
      }
      this.loadingClaims = true;
      try {
        var params = 'page=' + page + '&pageSize=' + this.pageSize +
          '&status=' + this.claimStatusFilter + '&type=' + this.claimTypeFilter +
          '&sortBy=' + this.sortBy + '&sortDesc=' + (this.sortDesc ? '1' : '0') +
          (withCounts ? '&withCounts=1' : '');
        var res = await axios.get('/api/edi/workflow/claims?' + params);
        this.claims = res.data.items;
        this.totalClaims = res.data.total;
        if (res.data.counts) this.statusCounts = res.data.counts;
        this.pageCache[key] = res.data.items;
        this.prefetchNeighbors(page);
      } catch (e) {
        this.showSnack('Failed to load claims', 'error');
      } finally {
        this.loadingClaims = false;
      }
    },
    prefetchNeighbors(page) {
      var self = this;
      var totalPages = Math.ceil(this.totalClaims / this.pageSize);
      for (var p = Math.max(1, page - 2); p <= Math.min(totalPages, page + 2); p++) {
        if (p === page) continue;
        (function(pg) {
          var key = self.cacheKey(pg, self.pageSize);
          if (!self.pageCache[key]) self.fetchForCache(pg);
        })(p);
      }
    },
    async fetchForCache(page) {
      var key = this.cacheKey(page, this.pageSize);
      if (this.pageCache[key]) return;
      this.pageCache[key] = [];  // mark as in-flight
      try {
        var params = 'page=' + page + '&pageSize=' + this.pageSize +
          '&status=' + this.claimStatusFilter + '&type=' + this.claimTypeFilter +
          '&sortBy=' + this.sortBy + '&sortDesc=' + (this.sortDesc ? '1' : '0');
        var res = await axios.get('/api/edi/workflow/claims?' + params);
        this.pageCache[key] = res.data.items;
      } catch (e) {
        delete this.pageCache[key];
      }
    },
    onPageChange(page) {
      var key = this.cacheKey(page, this.pageSize);
      if (this.pageCache[key] && this.pageCache[key].length > 0) {
        this.claims = this.pageCache[key];
        this.prefetchNeighbors(page);
      } else {
        this.fetchPage(page, false);
      }
    },
    onPageSizeChange(size) {
      this.pageCache = {};
      this.currentPage = 1;
      this.fetchPage(1, true);
    },
    onSortChange() {
      this.pageCache = {};
      this.currentPage = 1;
      this.$nextTick(function() { this.fetchPage(1, true); }.bind(this));
    },
    claimCount(status) {
      return this.statusCounts[status] || 0;
    },
    async retryErrors() {
      this.retryingErrors = true;
      try {
        var res = await axios.post('/api/edi/workflow/retry-errors');
        var result = await this.pollJob(res.data.jobId, null);
        if (result && result.message) {
          this.showSnack(result.message, 'info');
        } else if (result) {
          this.showSnack('Retried ' + result.retried + ' claims — ' + result.included + ' included in new batch', 'success');
        }
        await Promise.all([this.loadGeneratedFiles(), this.loadClaims()]);
      } catch (e) {
        this.showSnack((e.response && e.response.data && e.response.data.error) || e.message || 'Retry failed', 'error');
      } finally {
        this.retryingErrors = false;
      }
    },

    // ── Helpers ───────────────────────────────────────────────────────────
    batchStatusColor(s) {
      const map = { generated: 'blue lighten-2', submitted: 'blue', accepted: 'green', partial: 'orange', rejected: 'red', error: 'red darken-2' };
      return map[s] || 'grey';
    },
    claimStatusColor(s) {
      const map = { submitted: 'blue', completed: 'green', rejected: 'red', error: 'orange darken-1' };
      return map[s] || 'grey';
    },
    ik3Label(code) { return IK3[code] ? `${code} — ${IK3[code]}` : code; },
    ik4Label(code) { return IK4[code] || code; },
    formatDate(val) {
      if (!val) return '—';
      return new Date(val).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
    },
    formatSize(bytes) {
      if (!bytes) return '—';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },
    showSnack(text, color = 'success') {
      this.snack = { show: true, text, color };
    },
  },
  mounted() {
    this.loadRefStatus();
    this.loadGeneratedFiles();
    this.loadClaims();
  },
};
</script>
